import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Trophy, Medal, Printer, Download, Pencil, Save, X } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const FinalResultsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editPayout, setEditPayout] = useState("");
  const [editPoints, setEditPoints] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchResults();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results/final/${selectedPlayday}`);
      setResults(response.data);
    } catch (error) {
      toast.error("Failed to fetch results");
    }
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const getPlaceStyle = (place) => {
    if (place === 1) return "bg-yellow-100 text-yellow-800";
    if (place === 2) return "bg-gray-100 text-gray-800";
    if (place === 3) return "bg-orange-100 text-orange-800";
    return "";
  };

  const isRopingEvent = (eventName) => {
    return eventName?.includes("Team Roping") || eventName?.includes("Ribbon Roping");
  };

  // Transform results to show individual contestants for roping events
  const getDisplayResults = (eventResult) => {
    const isRoping = isRopingEvent(eventResult.event_name);
    const displayRows = [];
    
    eventResult.results.forEach((entry, idx) => {
      const place = idx + 1;
      
      // Always add contestant 1
      displayRows.push({
        id: entry.id,
        entry_id: entry.id,
        contestant_type: "contestant1",
        place,
        contestant_id: entry.contestant_id,
        contestant_name: entry.contestant_name,
        time: entry.time,
        penalty: entry.penalty,
        total: entry.total,
        payout: entry.payout,
        points: entry.points,
        isRoping,
        // For roping, show partner info
        partner_name: isRoping ? entry.contestant2_name : null
      });
      
      // For roping events, add contestant 2 as separate row
      if (isRoping && entry.contestant2_name) {
        displayRows.push({
          id: `${entry.id}_c2`,
          entry_id: entry.id,
          contestant_type: "contestant2",
          place,
          contestant_id: entry.contestant2_id,
          contestant_name: entry.contestant2_name,
          time: entry.time,
          penalty: entry.penalty,
          total: entry.total,
          payout: entry.contestant2_payout,
          points: entry.contestant2_points,
          isRoping,
          partner_name: entry.contestant_name
        });
      }
    });
    
    return displayRows;
  };

  const openEditDialog = (row, eventId) => {
    setEditingEntry({
      ...row,
      event_id: eventId
    });
    setEditPayout(row.payout?.toString() || "0");
    setEditPoints(row.points?.toString() || "0");
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/results/manual-update-individual`, {
        entry_id: editingEntry.entry_id,
        contestant_type: editingEntry.contestant_type,
        payout: parseFloat(editPayout) || 0,
        points: parseInt(editPoints) || 0
      });
      
      toast.success(`Updated ${editingEntry.contestant_name}'s payout and points`);
      setEditingEntry(null);
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleExportResults = () => {
    const exportData = [];
    results.forEach(eventResult => {
      const displayRows = getDisplayResults(eventResult);
      displayRows.forEach((row) => {
        exportData.push({
          event: eventResult.event_name,
          place: row.place,
          contestant: row.contestant_name,
          partner: row.partner_name || "",
          time: row.time?.toFixed(3) || "",
          penalty: row.penalty?.toFixed(1) || "",
          total: row.total?.toFixed(3) || "",
          payout: row.payout?.toFixed(2) || "0.00",
          points: row.points || 0
        });
      });
    });
    
    const headers = ["event", "place", "contestant", "partner", "time", "penalty", "total", "payout", "points"];
    
    exportToCSV(
      exportData, 
      `final_results_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      headers
    );
    toast.success("Final results exported to CSV");
  };

  const handlePrintResults = () => {
    handlePrint("Final Results", getSelectedPlaydayName());
  };

  const hasResults = results.some(r => r.results.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="final-results-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Final Results</h1>
          <p className="text-text-secondary mt-1">Individual contestant results - Click to edit payout/points</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintResults} disabled={!hasResults} data-testid="print-results-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportResults} disabled={!hasResults} data-testid="export-results-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Final Results</h1>
        <p>{getSelectedPlaydayName()}</p>
      </div>

      {/* Selector */}
      <div className="w-64 no-print">
        <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
        <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
          <SelectTrigger data-testid="playday-select">
            <SelectValue placeholder="Select a play day" />
          </SelectTrigger>
          <SelectContent>
            {playdays.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} ({p.date}) {p.is_finalized && "✓"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results by Event */}
      {results.length === 0 ? (
        <div className="bg-white rounded-xl border border-primary/20 p-12 text-center">
          <Trophy className="h-12 w-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary">No results available yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {results.map((eventResult) => {
            const isRoping = isRopingEvent(eventResult.event_name);
            const displayRows = getDisplayResults(eventResult);
            
            return (
              <div key={eventResult.event_id} className="bg-white rounded-xl border border-primary/20 overflow-hidden page-break-inside-avoid">
                <div className="p-4 border-b border-primary/20 bg-secondary/30">
                  <h3 className="font-heading font-bold text-xl text-text-primary flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-accent" />
                    {eventResult.event_name}
                    {isRoping && (
                      <span className="text-sm font-normal text-text-secondary ml-2">
                        (Each contestant listed individually)
                      </span>
                    )}
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading font-bold text-text-primary w-20">Place</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
                      {isRoping && (
                        <TableHead className="font-heading font-bold text-text-primary">Partner</TableHead>
                      )}
                      <TableHead className="font-heading font-bold text-text-primary text-right">Time</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Penalty</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Total</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Payout</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary text-right">Points</TableHead>
                      <TableHead className="font-heading font-bold text-text-primary w-20 no-print">Edit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isRoping ? 9 : 8} className="text-center py-8 text-text-secondary">
                          No results recorded
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayRows.map((row) => (
                        <TableRow 
                          key={row.id} 
                          className={`hover:bg-surface-muted ${getPlaceStyle(row.place)} ${row.contestant_type === "contestant2" ? "border-l-4 border-l-accent/50" : ""}`}
                        >
                          <TableCell className="font-heading font-bold text-lg">
                            <div className="flex items-center gap-2">
                              {row.place <= 3 && row.contestant_type === "contestant1" && <Medal className="h-4 w-4" />}
                              {row.place}
                              {row.contestant_type === "contestant2" && (
                                <span className="text-xs text-text-muted">(C2)</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{row.contestant_name}</TableCell>
                          {isRoping && (
                            <TableCell className="text-text-secondary text-sm">
                              {row.partner_name || "-"}
                            </TableCell>
                          )}
                          <TableCell className="text-right font-mono">
                            {row.time?.toFixed(3)}s
                          </TableCell>
                          <TableCell className="text-right font-mono text-status-error">
                            {row.penalty ? `+${row.penalty.toFixed(1)}s` : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {row.total?.toFixed(3)}s
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold text-status-success">
                            ${(row.payout || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-accent">
                            {row.points || 0}
                          </TableCell>
                          <TableCell className="no-print">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(row, eventResult.event_id)}
                              className="h-8 w-8 p-0"
                              data-testid={`edit-result-${row.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Edit Result - {editingEntry?.contestant_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payout ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={editPayout}
                onChange={(e) => setEditPayout(e.target.value)}
                placeholder="0.00"
                data-testid="edit-payout-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Points</Label>
              <Input
                type="number"
                value={editPoints}
                onChange={(e) => setEditPoints(e.target.value)}
                placeholder="0"
                data-testid="edit-points-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinalResultsPage;
